import React, { useRef, useState, useMemo, useEffect } from "react";
import osName from "../../../OsName";

const ROTATION_STRENGTH = osName() == 'PC' ? 0.01 : 0.02;
const SWIPE_THRESHOLD = 80;
const ANIM_DURATION = 300;

const styles = {
  segment:{
    position: "absolute",
    top: "18px",
    right: "25px",
    display: "flex",
    alignItems: "center",
    gap: "3.5px",
  },
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    background: "transparent",
    overflow: "hidden",
    userSelect: "none",
    touchAction: "none", 
    position: "relative",
  },
  container: {
    width: "100%",
    maxWidth: "720px",
    position: "relative",
    perspective: "1200px",
    height: "auto",
    cursor: "grab",
  },
  card: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    backfaceVisibility: "hidden",
  }
};

// 拆分出内部组件，确保 hooks 调用顺序始终一致
function HomeCardsInner({ data, renderItem }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animType, setAnimType] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const startXRef = useRef(0);
  const hasDraggedRef = useRef(false);
  const autoPlayTimerRef = useRef(null);

  const flyOutDistance = useMemo(() => {
    return osName() === 'PC' ? 750 : 400;
  }, []);

  const count = data.length;

  const triggerTransition = (dir) => {
    setIsAnimating(true);
    setAnimType(dir);
    
    const targetX = dir === -1 ? -flyOutDistance : flyOutDistance;
    setDragX(targetX);

    setTimeout(() => {
      setIsAnimating(false);
      setAnimType(0);
      setDragX(0);
      setActiveIndex(prev => (prev - dir + count) % count);
    }, ANIM_DURATION);
  };

  const clearAutoPlay = () => {
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
  };

  const startAutoPlay = () => {
    clearAutoPlay();
    if (count > 1) {
      autoPlayTimerRef.current = setTimeout(() => {
        triggerTransition(-1);
      }, 3000);
    }
  };

  // useEffect 现在始终会被调用，不受 count 影响
  useEffect(() => {
    startAutoPlay();
    return () => clearAutoPlay();
  }, [activeIndex, count]);

  const handlePointerDown = (e) => {
    if (isAnimating || count <= 1) return;
    clearAutoPlay();
    setIsDragging(true);
    hasDraggedRef.current = false;
    startXRef.current = e.clientX;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {}
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const deltaX = e.clientX - startXRef.current;
    setDragX(deltaX);
    if (Math.abs(deltaX) > 5) {
      hasDraggedRef.current = true;
      e.preventDefault();
    }
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {}

    if (dragX < -SWIPE_THRESHOLD) {
      triggerTransition(-1);
    } else if (dragX > SWIPE_THRESHOLD) {
      triggerTransition(1);
    } else {
      setIsAnimating(true);
      setDragX(0);
      setTimeout(() => {
        setIsAnimating(false);
        startAutoPlay();
      }, 300);
    }
  };

  const nextIdx = (activeIndex + 1) % count;
  const prevIdx = (activeIndex - 1 + count) % count;

  return (
    <div style={styles.wrapper}>
      <div 
        style={{
          ...styles.container,
          cursor: isDragging ? "grabbing" : "grab"
        }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div style={{ visibility: "hidden", pointerEvents: "none", width: "100%" }}>
          {renderItem ? renderItem(data[activeIndex], activeIndex) : null}
        </div>

        {[nextIdx, activeIndex, prevIdx].map((cardIdx, order) => {
          const item = data[cardIdx];
          const isNext = order === 0;
          const isCurrent = order === 1;
          const isPrev = order === 2;

          let transform = "";
          let opacity = 1;
          let zIndex = 0;

          const progress = Math.min(Math.abs(dragX) / (animType !== 0 ? flyOutDistance : 200), 1);

          if (dragX <= 0) {
            if (isPrev) {
              opacity = 0; 
            } else if (isNext) {
              zIndex = 5;
              const scale = 0.95 + (progress * 0.05);
              opacity = progress;
              transform = `scale(${scale})`;
            } else if (isCurrent) {
              zIndex = 10;
              const rotation = dragX * ROTATION_STRENGTH;
              transform = `translateX(${dragX}px) rotate(${rotation}deg)`;
              opacity = 1;
            }
          } else {
            if (isNext) {
              opacity = 0;
            } else if (isPrev) {
              zIndex = 20; 
              const startOffset = flyOutDistance; 
              const followX = -startOffset + dragX;
              const rotation = followX * ROTATION_STRENGTH;
              transform = `translateX(${followX}px) rotate(${rotation}deg)`;
              opacity = 1;
            } else if (isCurrent) {
              zIndex = 10;
              const scale = 1 - (progress * 0.05);
              transform = `scale(${scale})`;
              opacity = progress;
            }
          }

          return (
            <div
              key={cardIdx}
              onPointerDownCapture={handlePointerDown}
              onPointerMoveCapture={handlePointerMove}
              onPointerUpCapture={handlePointerUp}
              onPointerCancelCapture={handlePointerUp}
              style={{
                ...styles.card,
                transform,
                opacity,
                zIndex,
                pointerEvents: isDragging && hasDraggedRef.current ? "none" : "auto",
                transition: isAnimating 
                  ? (dragX <= 0 && isCurrent) || (dragX > 0 && isPrev)
                    ? `transform ${ANIM_DURATION}ms cubic-bezier(0.42, 0, 1, 1)`
                    : `transform ${ANIM_DURATION}ms cubic-bezier(0.42, 0, 1, 1), opacity ${ANIM_DURATION}ms ease-out`
                  : "none",
              }}
            >
              {renderItem ? renderItem(item, cardIdx) : null}
            </div>
          );
        })}
      </div>
      {data.length > 1 && (
        <div style={styles.segment}>
          {data.map((_, index) => (
            <img key={index} src={`resources/250627/${activeIndex === index ? "swiper-active" : "swiper-normal"}.png`} alt="" height="7"/>
          ))}
        </div>
      )}
    </div>
  );
}

// 外层包装组件处理空数据的早返回，内层组件 hooks 顺序永远一致
function HomeCards({ data = [], renderItem }) {
  if (!data || data.length === 0) return null;
  return <HomeCardsInner data={data} renderItem={renderItem} />;
}

export default HomeCards;